import type { ReactNode } from 'react'

import { cn } from './cn'

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'error'

const tones: Record<BadgeTone, string> = {
  neutral: 'bg-soft-grey-main text-soft-grey-contrast',
  primary: 'bg-primary-main text-primary-contrast',
  success: 'bg-success-main text-success-contrast',
  // An in-progress state, such as Incomplete.
  warning: 'bg-warning-main text-warning-contrast',
  error: 'bg-error-main text-error-contrast',
}

export interface BadgeProps {
  readonly tone?: BadgeTone
  /** The 24px pill with 12px text, for a count inside a button. */
  readonly isCompact?: boolean
  readonly className?: string
  readonly children: ReactNode
}

/**
 * A static count or status pill, such as "13 skills": design.md's chip, 29px tall with regular
 * 14px text. Chip is the interactive one. It grows when its text wraps, as in a narrow cell at 200%
 * zoom, so the text stays inside.
 */
export function Badge({ tone = 'neutral', isCompact = false, className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full',
        isCompact ? 'min-h-6 px-3 py-0.5 text-xs font-medium' : 'min-h-[29px] px-4 py-1 text-sm font-normal leading-[1.2]',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}
